// release-flow-candidate-push.ts
//
// The push step of the pre-G3 release flow (split out of
// supervisor-release-flow.ts, 200-line budget): push the candidate as a Draft MR
// — or REUSE the push the journal already records for this candidate and
// destination (round 2 M2).
//
// Reuse: once the flow waits for the MR pipeline (`waiting_ci`), "re-run" is the
// normal path, and re-deriving the candidate on every run made it fragile — the
// local target branch (shared with the operator's checkout) may have moved and
// stopped being an ancestor of the candidate (`push_failed`, a misleading
// "force-push" reason), or the release config changed and with it the
// `Autopilot-Deploy:` trailer, i.e. a different pushed SHA on the same parent
// (non-fast-forward reject). A recorded push is bound as it was made: its
// `pushed_sha` and the `base_sha` it was built on (intents from older CLIs carry
// no base — the caller's base is then used). No recorded push (first run, push
// failed, other remote/branch/target, a NEW candidate, an intent without a
// result) ⇒ push. Reuse gives up self-healing: if the branch/MR no longer matches
// the recorded push, the messages say so (round 3 M-B) instead of re-pushing.
//
// Second candidate (round 3 M-A): a redirect/new HEAD yields a sibling commit on
// the base, which a plain push to the existing `candidate/<mission>` branch
// rejects as non-fast-forward. When the journal records an earlier successful
// push to this destination, the new push carries `--force-with-lease` on that
// commit — the branch is overwritten only while it still holds exactly what we
// pushed. No recorded push ⇒ the plain push, as before. Decided up front, not
// "after a rejection", so no git stderr text has to be parsed.
import { pushDraftMergeRequest } from "./draft-merge-request-push.js";
import {
  findLastPushToDestination,
  findRecordedPush,
  type RecordedPush,
} from "./recorded-draft-mr-push.js";
import type { ReleaseFlowUpToG3Input } from "./supervisor-release-flow.js";

export type CandidatePush =
  | {
      status: "ready";
      pushedSha: string;
      /** The base the pushed commit was built on (what X2 compares the MR's base to). */
      baseSha: string;
      stdout: string;
      /** When the push was recorded (a fresh push: when it completed); `undefined` for an older CLI's. */
      recordedAtMs: number | undefined;
      /** Reuse only: appended to evidence/binding failures — the push was NOT repeated (M-B). */
      reuseNote?: string;
      /** Reuse only: appended to the G3 line when the deploy recipe changed since the push (M-C). */
      warning?: string;
    }
  | { status: "blocked_push_policy"; reason: string }
  | { status: "push_failed"; reason: string };

function reuseNote(recorded: RecordedPush, remote: string, branch: string): string {
  const when =
    recorded.recordedAtMs === undefined
      ? ""
      : ` (ghi lúc ${new Date(recorded.recordedAtMs).toISOString()})`;
  return (
    `Lưu ý: commit ${recorded.pushedSha} đã được push ở lần chạy trước${when} và KHÔNG push lại lần này ` +
    "(flow dùng lại lần push ghi trong journal), nên `run` lại không tự sửa nhánh/MR. Nếu nhánh " +
    `\`${branch}\` bị xoá hoặc ai đó đã đẩy commit khác lên: khôi phục nhánh về đúng commit đó (vd ` +
    `\`git push ${remote} ${recorded.pushedSha}:refs/heads/${branch}\` từ workspace của mission) rồi \`run\` lại; ` +
    'hoặc `avatar autopilot redirect --text "<mô tả>"` để dựng candidate mới (chỉ đẩy lên được khi nhánh còn ' +
    "ở commit đã ghi); hoặc `avatar autopilot archive` để bỏ mission."
  );
}

/** M-C: the MR trailer says one thing, config.release now another; the G3 line is authoritative. */
function recipeWarning(recorded: RecordedPush, current: string | null): string | undefined {
  if (recorded.deployRecipeId === undefined || recorded.deployRecipeId === current)
    return undefined;
  const was =
    recorded.deployRecipeId === null ? "not-configured" : `recipe ${recorded.deployRecipeId}`;
  const now = current === null ? "chưa khai deploy_recipe" : `khai deploy_recipe "${current}"`;
  return (
    `Cảnh báo: Draft MR đang mang trailer "Autopilot-Deploy: ${was}" (ghi lúc push ở lần chạy trước) nhưng ` +
    `config.release hiện ${now} — dòng G3 này mới là nguồn sự thật về việc deploy, không phải description của MR.`
  );
}

export async function pushOrReuseCandidate(
  input: ReleaseFlowUpToG3Input,
  nowMs: () => number,
): Promise<CandidatePush> {
  const { remote, branch, target } = input.pushInput;
  const destination = { remote, branch, target };
  const recorded = findRecordedPush(input.store.state, input.candidateSha, destination);
  if (recorded !== undefined) {
    const warning = recipeWarning(recorded, input.pushInput.deployRecipeId ?? null);
    return {
      status: "ready",
      pushedSha: recorded.pushedSha,
      baseSha: recorded.baseSha ?? input.baseSha,
      stdout: "",
      recordedAtMs: recorded.recordedAtMs,
      reuseNote: reuseNote(recorded, remote, branch),
      ...(warning === undefined ? {} : { warning }),
    };
  }
  const lastPush = findLastPushToDestination(input.store.state, destination);
  const pushResult = await pushDraftMergeRequest({
    ...input.pushInput,
    store: input.store,
    repoDir: input.repoDir,
    candidateSha: input.candidateSha,
    baseSha: input.baseSha,
    policyDigest: input.policyDigest,
    missionId: input.missionId,
    ...(lastPush === undefined ? {} : { forceWithLeaseSha: lastPush.pushedSha }),
  });
  if (pushResult.status === "policy_denied") {
    return { status: "blocked_push_policy", reason: pushResult.reason };
  }
  if (pushResult.status === "push_failed") {
    return { status: "push_failed", reason: pushResult.stderr };
  }
  return {
    status: "ready",
    pushedSha: pushResult.pushedSha,
    baseSha: input.baseSha,
    stdout: pushResult.stdout,
    // Round 3 L-3: the "no pipeline yet" window opens when the push COMPLETED.
    recordedAtMs: nowMs(),
  };
}
